import { HTMLProps, useCallback, useEffect, useMemo, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import classNames from "classnames";

import { Button } from "react-migration/components/DeprecatedButton";
import { Icon } from "react-migration/components/Icon";
import { TextInputV2 } from "../TextInputV2";

import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { ActionBar } from "./ActionBar";
import { SelectOptionItem } from "./SelectOptionItem";
import { ModalV2 } from "../ModalV2/ModalV2";

export type SelectOption = {
  id: string;
  name: string;
  color?: string;
};

export type SelectFilterPopoverProps = {
  label: string;
  labelClassName?: string;
  contentTitle: string;
  options: SelectOption[];
  selectedOptionIds: string[];
  onSelectedOptionsUpdate: (optionIds: string[]) => void;
  className?: string;
  dataTestId?: string;
};

export const SelectFilterPopover = ({
  label,
  labelClassName,
  contentTitle,
  options,
  selectedOptionIds,
  onSelectedOptionsUpdate,
  className,
  dataTestId = "select-filter",
}: SelectFilterPopoverProps) => {
  const [open, setOpen] = useState(false);

  return (
    <div data-testid={dataTestId} className={classNames("flex", "flex-row", "flex-nowrap", "items-center", className)}>
      <span
        className={classNames("text-text-base-secondary-a", "mr-2", labelClassName)}
        data-testid="select-filter-label"
      >
        {label}
      </span>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger data-testid="select-filter-dropdown-trigger" asChild>
          <div>
            <SelectFilterStatus selectedOptionIds={selectedOptionIds} />
          </div>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            data-testid="select-filter-dropdown-content"
            sideOffset={5}
            side="bottom"
            align="start"
            asChild
          >
            <div className="z-10 bg-white rounded-lg shadow-md">
              <div data-testid="select-filter-dropdown-title" className="flex flex-col p-3 m-1">
                <h1 className="text-sm font-semibold truncate cursor-default text-content-primary">{contentTitle}</h1>
              </div>
              <SelectFilterContent
                options={options}
                selectedOptionIds={selectedOptionIds}
                onSelectedOptionsUpdate={onSelectedOptionsUpdate}
                setOpen={setOpen}
                className="flex flex-col overflow-hidden w-[300px] max-h-[444px]"
              />
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

export type SelectFilterModalProps = {
  contentTitle: string;
  options: SelectOption[];
  selectedOptionIds: string[];
  onSelectedOptionsUpdate: (optionIds: string[]) => void;
  open: boolean;
  setOpen: (v: boolean) => void;
};
export const SelectFilterModal = ({
  contentTitle,
  options,
  selectedOptionIds,
  onSelectedOptionsUpdate,
  open,
  setOpen,
}: SelectFilterModalProps) => {
  return (
    <ModalV2.Root open={open} onOpenChange={() => setOpen(false)}>
      <ModalV2.Body title={contentTitle} className="z-1000!" contentClassName="max-w-[600px]!">
        <SelectFilterContent
          options={options}
          selectedOptionIds={selectedOptionIds}
          onSelectedOptionsUpdate={onSelectedOptionsUpdate}
          setOpen={setOpen}
          className="flex flex-col overflow-hidden max-h-[60vh]"
        />
      </ModalV2.Body>
    </ModalV2.Root>
  );
};

export const SelectFilterStatus = ({ selectedOptionIds }: { selectedOptionIds: string[] }) => {
  const { t } = useTranslation();

  const triggerText = useMemo(() => {
    if (!selectedOptionIds.length) {
      return t("sites.pipeline.filters.all");
    }
    return t("sites.pipeline.filters.number_of_filters_selected", {
      selectedFiltersCount: selectedOptionIds.length,
    });
  }, [selectedOptionIds.length, t]);

  return (
    <span
      className={classNames(
        "font-semibold",
        "px-2",
        "py-1",
        "rounded-sm",
        "text-content-primary",
        "flex",
        "items-center",
        "justify-center",
        "hover:bg-background-button-secondary-hover"
      )}
    >
      {triggerText}
      <Icon
        cursor="pointer"
        data-testid="site-options-trigger-icon"
        icon="icon-lt-arrow-down-s-fill"
        size="sm"
        extraClasses="ml-1"
      />
    </span>
  );
};

export type SelectFilterContentProps = HTMLProps<HTMLDivElement> & {
  options: SelectOption[];
  selectedOptionIds: string[];
  onSelectedOptionsUpdate: (optionIds: string[]) => void;
  setOpen?: (v: boolean) => void;
};

export const SelectFilterContent = ({
  options,
  selectedOptionIds,
  onSelectedOptionsUpdate,
  setOpen,
  ...rest
}: SelectFilterContentProps) => {
  const { t } = useTranslation();

  const [filterValue, setFilterValue] = useState("");
  const [activeOptionIds, setActiveOptionIds] = useState<string[]>(selectedOptionIds);

  useEffect(() => {
    setActiveOptionIds(selectedOptionIds);
  }, [selectedOptionIds]);

  const onCancel = () => {
    setActiveOptionIds(selectedOptionIds);
    setOpen && setOpen(false);
  };

  const onApply = () => {
    onSelectedOptionsUpdate(options.map(({ id }) => id).filter((id) => activeOptionIds.includes(id)));
    setOpen && setOpen(false);
  };

  const onClearFilterClick = () => {
    setActiveOptionIds([]);
  };

  const onSelectAllToggle = useCallback(() => {
    if (activeOptionIds.length !== options.length) {
      setActiveOptionIds(options.map(({ id }) => id));
    } else {
      setActiveOptionIds([]);
    }
  }, [activeOptionIds.length, options]);

  const onOptionToggle = useCallback(
    (optionId: string) => {
      if (activeOptionIds.includes(optionId)) {
        setActiveOptionIds(activeOptionIds.filter((id) => id !== optionId));
      } else {
        setActiveOptionIds([...activeOptionIds, optionId]);
      }
    },
    [activeOptionIds]
  );

  return (
    <div {...rest}>
      <div data-testid="select-filter-search" className="px-3 pt-0 pb-2">
        <TextInputV2
          size="medium"
          dataTestId="select-filter-search-input"
          placeholder={t("sites.pipeline.filters.search")}
          IconLeft={<i className="text-base search-icon icon-lt-search-line" />}
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
        />
      </div>

      <div className="justify-start">
        <Button.Borderless
          data-testid="select-filter-clear-button"
          disabled={activeOptionIds.length === 0}
          className={classNames("text-content-action", "my-2")}
          onClick={onClearFilterClick}
        >
          {t("sites.pipeline.filters.clear_filter")}
        </Button.Borderless>
      </div>

      <SelectOptionItem
        id="all"
        name={t("sites.pipeline.filters.select_all")}
        isSelected={activeOptionIds.length === options.length}
        onToggle={onSelectAllToggle}
      />

      <div
        className={classNames("pl-4", "flex", "overflow-scroll", "flex-col")}
        data-testid="select-filter-option-list"
      >
        {options
          .filter(({ name }) => {
            const filter = new RegExp(filterValue, "i");
            return filter.test(name);
          })
          .sort(({ name: a }, { name: b }) => {
            if (a < b) return -1;
            if (a > b) return 1;
            return 0;
          })
          .map(({ id, name, color }) => (
            <SelectOptionItem
              key={id}
              id={id}
              name={name}
              color={color}
              isSelected={activeOptionIds.includes(id)}
              onToggle={onOptionToggle}
            />
          ))}
      </div>
      <ActionBar onApply={onApply} onCancel={onCancel} />
    </div>
  );
};
